import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';
import { formatCurrency } from 'utils/NumberHelper';

import { ProductTypeLabel } from 'enums/product-type';
import { CreditCards } from 'enums/credit-cards-type';
import { FacilitiesLabel, FacilitiesSource, FacilitiesType } from 'enums/facilities-types';
import { Banks, Products } from 'enums/banks';

import CreditLineFacilityAccountInfo from './CreditLineFacilityAccountInfoModel';
import { SubProductType, SubProductTypeLabel } from 'enums/subproduct-type';

const FacilityRecord = Record({
  id: '',
  entityId: '',
  account: null,
  interestAccount: null,
  providerId: 0,
  providerName: '',
  fundingSearchId: 0,
  productType: 0,
  productId: null,
  productName: '',
  subProductName: '',
  amount: 0,
  lenderAmount: 0,
  utilisedAmount: 0,
  utilisedMargin: 0,
  overdraftLimit: 0,
  period: 0,
  startDate: new Date(0),
  endDate: null,
  logoUrl: '',
  iconUrl: '',
  isBankAccount: null,
  isDeleted: false,
  sortCode: '',
  cardholderName: '',
  monthlyRepayment: 0,
  totalRepayment: 0,
  rate: 0,
  sweep: '',
  ratio: null,
  ltv: null,
  fees: null,
  mortgageTerm: null,
  rateType: '',
  interestFreePeriod: new Date(0),
  capitalRepaymentHoliday: new Date(0),
  type: '',
  status: null,
  hasMortgage: null,
  hasOverdraft: null,
  source: null,
});

export default class Facility extends FacilityRecord {
  static deserialize(jsonObject = {}) {
    const mapProperties = {
      interestFreePeriod: Hydrator.timestamp2Date,
      capitalRepaymentHoliday: Hydrator.timestamp2Date,
      startDate: Hydrator.timestamp2Date,
      endDate: Hydrator.timestamp2Date,
      account: CreditLineFacilityAccountInfo.deserialize,
      interestAccount: CreditLineFacilityAccountInfo.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new Facility(hydrated);
  }

  get productTypeLabel() {
    return ProductTypeLabel[this.productType];
  }

  get isCreditCard() {
    return !!CreditCards[this.providerName];
  }

  get getProductName() {
    if (this.hasMortgage) return Products.mortgage;

    return this.hasOverdraft ? Products.overdraft : Products.noOverdraft;
  }

  get getType() {
    if (!this.isBankAccount) {
      return FacilitiesType.creditLines;
    }

    if (this.isCreditCard) {
      return FacilitiesType.creditCards;
    }

    return FacilitiesType.bankAccounts;
  }

  get balance() {
    if (this.getType === FacilitiesType.creditLines) {
      return Math.abs(this.lenderAmount);
    }
    return this.lenderAmount;
  }

  get formattedOverdraftLimit() {
    return this.overdraftLimit ? formatCurrency(this.overdraftLimit) : '-';
  }

  get name() {
    let name = '';
    if (this.source === FacilitiesSource.publicAccountingProvider) {
      return SubProductTypeLabel?.[SubProductType?.[this.subProductName]] ?? this.subProductName;
    }
    switch (this.getType) {
      case FacilitiesType.creditCards:
        name = CreditCards[this.providerName];
        break;
      case FacilitiesType.bankAccounts:
        name = Banks[this.providerName];
        break;
      default:
        name = this.providerName;
    }
    if (!name || name === '') {
      return this.account?.name;
    }
    return name;
  }

  get description() {
    switch (this.getType) {
      case FacilitiesType.creditCards:
        return FacilitiesLabel[FacilitiesType.creditCards];
      case FacilitiesType.bankAccounts:
        return this.overdraftLimit ? 'Overdraft' : 'No Overdraft';
      default:
        return (
          SubProductTypeLabel[SubProductType[this.subProductName]] ||
          ProductTypeLabel[this.productId]
        );
    }
  }
}
